import {makeAutoObservable, observable} from 'mobx';

import {Visit} from '@/domain/models/Visit';
import {Visitor} from '@/domain/models/Visitor';
import queryClient from '@/shared/lib/react-query/queryClient';

import {FEATURES_QUERY_VISITS} from '../consts/queryKeys';

class VisitsStore {
  list = observable.array<Visit>([], {deep: false});
  visitors = observable.map<Visitor['id'], Visitor>([], {deep: false});
  count = 0;
  countVisitors = 0;
  fetchedAllVisits: boolean = false;
  private _shouldRefresh: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  get shouldRefresh() {
    return this._shouldRefresh;
  }

  setShouldRefresh(shouldRefresh: boolean) {
    this._shouldRefresh = shouldRefresh;
  }

  get empty() {
    return this.list.length === 0 && this.fetchedAllVisits;
  }

  get lastLoadedVisitAt() {
    return this.list[this.list.length - 1]?.created_at;
  }

  get lastThreeVisits() {
    return this.list.slice(0, 3);
  }

  setFetchedAllVisits(fetched: boolean) {
    this.fetchedAllVisits = fetched;
  }

  addVisits(list: Visit[]) {
    if (list.length === 0) {
      return;
    }

    const existingIds = new Set(this.list.map((item) => item.created_at));
    const nextItems = list.filter((item) => !existingIds.has(item.created_at));

    if (nextItems.length > 0) {
      this.list.push(...nextItems);
    }
  }

  applyLatestVisits(list: Visit[]): boolean {
    const topVisit = this.list[0];

    if (topVisit) {
      const topVisitIndex = list.findIndex(
        (item) => item.created_at === topVisit.created_at
      );

      if (topVisitIndex >= 0) {
        this.list.unshift(...list.slice(0, topVisitIndex));
        return true;
      }
    }

    this.list.replace(list);
    this.fetchedAllVisits = false;

    return false;
  }

  addVisit(visit: Visit) {
    this.list.unshift(visit);
    this.count++;
  }

  setCount(count: number) {
    this.count = count;
  }

  getVisitor(id?: number) {
    return id ? this.visitors.get(id) : undefined;
  }

  addVisitors(visitors: Visitor[]) {
    for (const visitor of visitors) {
      this.addVisitor(visitor);
    }
  }

  addVisitor(visitor: Visitor) {
    this.visitors.set(visitor.id, visitor);
  }

  removeByUser(userId: number) {
    const nextList = this.list.filter((visit) => visit.user_id !== userId);
    const removedCount = this.list.length - nextList.length;

    if (removedCount === 0) {
      return;
    }

    this.list.replace(nextList);
    this.count = Math.max(0, this.count - removedCount);
  }

  removeByVisitor(visitorId: number) {
    const nextList = this.list.filter(
      (visit) => visit.visitor_id !== visitorId
    );
    const removedCount = this.list.length - nextList.length;

    if (removedCount > 0) {
      this.list.replace(nextList);
      this.count = Math.max(0, this.count - removedCount);
    }

    if (this.visitors.has(visitorId)) {
      this.countVisitors = Math.max(0, this.countVisitors - 1);
    }
    this.visitors.delete(visitorId);
  }

  setCountVisitors(totalVisitors: number) {
    this.countVisitors = totalVisitors;
  }

  resetList() {
    this.list.clear();
    this.visitors.clear();
    this.fetchedAllVisits = false;
    this.dropCache();
  }

  dropCache() {
    queryClient.removeQueries({
      queryKey: [FEATURES_QUERY_VISITS],
    });
  }
}

export const visitsStore = new VisitsStore();
